import React, { useCallback, useEffect, useRef } from 'react';

import { useInboundCrossDockServiceFee } from 'components/logistics/components/crossDockPurchaseOrders/useInboundCrossDockServiceFee';
import {
    applyInboundCrossDockServiceFeeRate,
    buildDefaultServiceFeeRow,
    getRequiredServiceFeeRow,
    updateAdditionalFeeRow,
} from 'components/logistics/components/crossDockPurchaseOrders/utils/crossDockPurchaseOrderAdditionalFees';
import {
    getRequiredAdditionalFeeChargeType,
    getRequiredAdditionalFeeLabel,
} from 'components/logistics/components/crossDockPurchaseOrders/utils/crossDockPurchaseOrderAdditionalFeeTypeUtils';
import { formatCurrency } from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDisplayUtils';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';

const labelClassName = 'text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground';

/**
 * @param {Object} props
 * @param {Object} props.draft
 * @param {(updater: (prev: Object) => Object) => void} [props.onDraftChange]
 * @param {Array<{ chargeType: string, label: string, required: boolean }>} [props.additionalFeeTypes]
 * @param {boolean} [props.showValidation]
 * @param {Object} [props.feeFieldErrors]
 * @param {boolean} [props.readOnly=false]
 */
export function CrossDockPurchaseOrderServiceFeeSection({
    additionalFeeTypes = [],
    draft,
    feeFieldErrors = {},
    onDraftChange,
    readOnly = false,
    showValidation = false,
}) {
    const lastAppliedCrossDockIdRef = useRef(null);
    const skippedPersistedHydrationRef = useRef(false);

    const requiredChargeType = getRequiredAdditionalFeeChargeType(additionalFeeTypes);
    const serviceFeeRow = getRequiredServiceFeeRow(draft?.additionalFees, requiredChargeType);
    const hasPersistedFeeRows = (draft?.additionalFees ?? []).some((fee) => fee.id != null);
    // Use isLoading (not isFetching) so a background refetch does not block applying a cached serviceFee.
    const { data: inboundServiceFee = 0, isLoading: isServiceFeeLoading } = useInboundCrossDockServiceFee(
        draft?.crossDockId,
        { enabled: !readOnly },
    );

    useEffect(() => {
        if (readOnly || !requiredChargeType || typeof onDraftChange !== 'function') {
            return;
        }
        onDraftChange((prev) => {
            if (getRequiredServiceFeeRow(prev.additionalFees, requiredChargeType)) {
                return prev;
            }
            return {
                ...prev,
                additionalFees: [buildDefaultServiceFeeRow(requiredChargeType), ...(prev.additionalFees ?? [])],
            };
        });
    }, [onDraftChange, readOnly, requiredChargeType]);

    useEffect(() => {
        if (readOnly || typeof onDraftChange !== 'function' || isServiceFeeLoading) {
            return;
        }
        const crossDockId = Number(draft?.crossDockId);
        if (!requiredChargeType || !Number.isFinite(crossDockId) || crossDockId <= 0) {
            return;
        }
        if (lastAppliedCrossDockIdRef.current === crossDockId) {
            return;
        }
        // Preserve saved base fee on first hydrate of a persisted order.
        if (hasPersistedFeeRows && !skippedPersistedHydrationRef.current) {
            skippedPersistedHydrationRef.current = true;
            lastAppliedCrossDockIdRef.current = crossDockId;
            return;
        }
        lastAppliedCrossDockIdRef.current = crossDockId;
        onDraftChange((prev) => applyInboundCrossDockServiceFeeRate(prev, inboundServiceFee, requiredChargeType));
    }, [
        draft?.crossDockId,
        hasPersistedFeeRows,
        inboundServiceFee,
        isServiceFeeLoading,
        onDraftChange,
        readOnly,
        requiredChargeType,
    ]);

    const handleChange = useCallback(
        (field) => (event) => {
            if (readOnly || !requiredChargeType || typeof onDraftChange !== 'function') {
                return;
            }
            onDraftChange((prev) => ({
                ...prev,
                additionalFees: updateAdditionalFeeRow(prev.additionalFees, requiredChargeType, {
                    [field]: event?.target?.value ?? '',
                }),
            }));
        },
        [onDraftChange, readOnly, requiredChargeType],
    );

    const quantity = serviceFeeRow?.quantity ?? '';
    const unitRate = serviceFeeRow?.unitRate ?? '';
    const totalRate = serviceFeeRow?.totalRate ?? 0;
    const quantityError =
        showValidation && !readOnly && requiredChargeType
            ? (feeFieldErrors?.[`fee.${requiredChargeType}.quantity`] ?? '')
            : '';
    const unitRateError =
        showValidation && !readOnly && requiredChargeType
            ? (feeFieldErrors?.[`fee.${requiredChargeType}.unitRate`] ?? '')
            : '';
    const formula = `${quantity === '' || quantity == null ? '—' : quantity} × ${
        unitRate === '' || unitRate == null ? formatCurrency(null) : formatCurrency(unitRate)
    } = ${formatCurrency(totalRate)}`;

    return (
        <div className="rounded-md border p-4">
            <div className="mb-4 text-sm font-semibold text-foreground">
                {getRequiredAdditionalFeeLabel(additionalFeeTypes)}
            </div>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:gap-6">
                <div className="flex min-w-0 flex-1 flex-col gap-2">
                    <Label htmlFor="serviceFeeNumPallets" className={labelClassName}>
                        Num Pallets
                    </Label>
                    {readOnly ? (
                        <span className="text-sm font-medium text-foreground">
                            {quantity === '' || quantity == null ? '—' : String(quantity)}
                        </span>
                    ) : (
                        <>
                            <Input
                                id="serviceFeeNumPallets"
                                name="serviceFeeNumPallets"
                                type="number"
                                inputMode="numeric"
                                min="1"
                                step="1"
                                value={quantity}
                                onChange={handleChange('quantity')}
                                aria-invalid={Boolean(quantityError)}
                            />
                            {quantityError ? <p className="text-xs text-destructive">{quantityError}</p> : null}
                        </>
                    )}
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-2">
                    <Label htmlFor="serviceFeeBaseFee" className={labelClassName}>
                        Base Fee
                    </Label>
                    {readOnly ? (
                        <span className="text-sm font-medium text-foreground">
                            {unitRate === '' || unitRate == null ? formatCurrency(null) : formatCurrency(unitRate)}
                        </span>
                    ) : (
                        <>
                            <Input
                                id="serviceFeeBaseFee"
                                name="serviceFeeBaseFee"
                                type="number"
                                inputMode="decimal"
                                min="0"
                                step="0.01"
                                value={unitRate}
                                onChange={handleChange('unitRate')}
                                aria-invalid={Boolean(unitRateError)}
                            />
                            {unitRateError ? <p className="text-xs text-destructive">{unitRateError}</p> : null}
                        </>
                    )}
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-2 sm:pb-2">
                    <span className={labelClassName}>Total</span>
                    <span className="text-sm font-medium text-foreground" data-testid="service-fee-total-formula">
                        {formula}
                    </span>
                </div>
            </div>
        </div>
    );
}

CrossDockPurchaseOrderServiceFeeSection.displayName = 'CrossDockPurchaseOrderServiceFeeSection';
